/**
 * ÉCHELLES ET FORMATS — la partie du graphique et du compteur qui ne dépend
 * ni de React, ni du DOM.
 *
 * Isolée ici pour être RÉELLEMENT testable : `Chart.tsx` et `lib.tsx` tirent
 * React et des icônes, que la suite de tests n'a pas à charger pour vérifier
 * qu'un axe est gradué en nombres ronds ou qu'un compteur ne fabrique pas de
 * valeurs intermédiaires.
 */

/** Nature de la grandeur — décide du formatage de l'axe et du panneau. */
export type ChartValueFormat = 'integer' | 'currency'

/**
 * GRADUATIONS LISIBLES — des nombres ronds, jamais « 93,6 ».
 *
 * Le pas est choisi dans 1 · 2 · 2,5 · 5 × 10ⁿ, la famille que l'œil lit sans
 * effort. On vise 4 intervalles : au-delà de 6, l'axe devient une échelle de
 * laboratoire.
 *
 * La ligne de base est TOUJOURS zéro. Cadrer sur 81–94 aurait fait ressembler
 * une progression de treize billets à une explosion ; sur des comptes cumulés
 * positifs, l'origine fait partie de l'information.
 */
export function niceScale(max: number, cible = 4): { max: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { max: 1, ticks: [0, 1] }
  const brut = max / cible
  const magnitude = Math.pow(10, Math.floor(Math.log10(brut)))
  const norm = brut / magnitude
  const pas = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude
  const haut = Math.ceil(max / pas) * pas
  const ticks: number[] = []
  for (let v = 0; v <= haut + pas / 2; v += pas) ticks.push(Number(v.toFixed(6)))
  return { max: haut, ticks }
}

/**
 * Formatage court : l'axe n'est pas un tableau comptable.
 *
 * LE CHIFFRE D'AFFAIRES ARRIVE EN CENTIMES. C'est l'unité du domaine
 * (`event_ticket.price_cents`), et l'API la transmet telle quelle. Tant que le
 * graphique n'avait pas d'axe, l'unité ne se voyait pas : la courbe a la même
 * forme en euros ou en centimes. Le premier axe affiché a montré « 150 k € »
 * pour une soirée à 1 500 € — faux de deux ordres de grandeur. La conversion
 * se fait ICI, au seul endroit où une somme devient du texte, pour que l'axe,
 * le panneau de survol et la légende ne puissent pas diverger.
 */
export function formatValue(v: number, format: ChartValueFormat, compact = false): string {
  if (format === 'currency') {
    const euros = v / 100
    // `toFixed` produit un POINT décimal : en français c'est une virgule.
    if (compact && Math.abs(euros) >= 1000) {
      return `${(euros / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k€`
    }
    // Pas de centimes affichés quand il n'y en a pas : « 1 250 € » se lit,
    // « 1 250,00 € » se déchiffre. Une graduation à 2,50 € les garde.
    return `${new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: Number.isInteger(euros) ? 0 : 2,
      maximumFractionDigits: Number.isInteger(euros) ? 0 : 2,
    }).format(euros)} €`
  }
  if (compact && Math.abs(v) >= 10000) {
    return `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} k`
  }
  // Une valeur entière ne prend pas de décimale ; une graduation à 2,5 en garde une.
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: Number.isInteger(v) ? 0 : 1 }).format(v)
}

/* ═══════════════ LE GRAND NOMBRE VIVANT ═══════════════ */

export interface NumberState {
  /** Valeur qui SORT de l'écran. `null` = première apparition. */
  prev: number | null
  /** Valeur affichée. */
  cur: number | null
  /** 1 = monte, -1 = descend, 0 = première apparition ou indéterminé. */
  dir: number
  /** Incrémenté à chaque transition — c'est lui qui rejoue l'animation. */
  seq: number
}

/**
 * La transition du grand nombre. PURE.
 *
 * Trois refus, qui viennent de ce que ce chiffre SIGNIFIE :
 *
 *  · PAS DE COMPTAGE DEPUIS ZÉRO. Faire défiler 0 → 81 est spectaculaire et
 *    faux : personne n'a observé 37 billets scannés.
 *  · PAS DE VALEURS INTERMÉDIAIRES. Une synchronisation qui passe de 81 à 89
 *    n'a jamais constaté 82, 83, 84 — le nouveau chiffre remplace l'ancien.
 *  · PAS D'ANIMATION SANS NOUVELLE VALEUR. Un rendu React, un retour d'onglet
 *    ou un changement de mesure ailleurs sur la page ne réveillent rien.
 *
 * Et une neutralité : une baisse (correction de source) s'anime dans l'autre
 * sens, sans couleur de jugement. Toute variation n'est pas un succès.
 */
export function nextNumberState(etat: NumberState, valeur: number | null): NumberState {
  if (etat.cur === valeur) return etat
  if (etat.cur === null || valeur === null) {
    return { prev: null, cur: valeur, dir: 0, seq: etat.seq + 1 }
  }
  return { prev: etat.cur, cur: valeur, dir: valeur > etat.cur ? 1 : -1, seq: etat.seq + 1 }
}

/* ═══════════════════════════ MAINTENANT SUR L'AXE ═══════════════════════ */

/** Largeur du repère SVG du graphique — voir `Chart.tsx`. */
export const CHART_W = 720
export const CHART_PAD_X = 6

export interface ChartAxis {
  /** Début du premier bucket. */
  t0: number
  /** Fin du dernier bucket. */
  tEnd: number
  /** Étendue horizontale réellement tracée (0 = pas d'axe). */
  spanMs: number
}

/**
 * FRACTION DE L'AXE OCCUPÉE PAR UN INSTANT — ou `null`.
 *
 * `null` dès que l'instant tombe hors des bornes réellement tracées, ou que
 * l'axe n'a aucune étendue. Sans ce refus, la valeur serait bornée et le
 * repère se collerait à un bord : le graphique affirmerait « maintenant,
 * c'est ici » à un endroit où il n'est pas.
 *
 * Le placement dépend du TYPE de tracé, et l'appelant l'a déjà pris en compte
 * dans `spanMs` : une courbe pose ses points AU début de chaque bucket (son
 * étendue s'arrête donc au début du dernier), une barre OCCUPE le bucket
 * entier.
 */
export function nowFraction(axis: ChartAxis | null, nowMs: number | null | undefined): number | null {
  if (!axis || nowMs === null || nowMs === undefined) return null
  if (axis.spanMs <= 0) return null
  if (nowMs < axis.t0 || nowMs > axis.tEnd) return null
  const inner = Math.max(0, Math.min(1, (nowMs - axis.t0) / axis.spanMs))
  return (CHART_PAD_X + inner * (CHART_W - CHART_PAD_X * 2)) / CHART_W
}

/* ═══════════════════ L'AXE DU TEMPS : JOURS DE PARIS (19/10/2026) ═══════════ */

const PARIS = 'Europe/Paris'
const jourParis = (ms: number): string =>
  new Date(ms).toLocaleDateString('en-CA', { timeZone: PARIS })

/**
 * LA PLACE D'UN POINT SUR L'AXE — l'intervalle le plus proche, pas une
 * égalité exacte. Les jours se coupent à minuit À PARIS : autour du passage à
 * l'heure d'hiver (25/10/2026), un jour dure 25 h, et « début + i × 24 h »
 * ne retombait plus sur la date du serveur — le point disparaissait sans un
 * mot. Une référence N-1 décalée de quelques minutes retrouve aussi son
 * intervalle.
 */
export function indexDuBucket(tMs: number, t0: number, bucketS: number): number {
  return Math.round((tMs - t0) / (bucketS * 1000))
}

/**
 * Le jour figure sur les heures de l'axe quand les ventes s'étalent sur
 * plusieurs jours : MEDECINE a vendu du 14/10 15 h au 16/10 2 h, et « 14:00 »
 * revenait deux fois. Pas au pas de la demi-heure d'une nuit (les entrées
 * franchissent minuit sans ambiguïté).
 */
export function jourSurLAxe(t0: number, tEnd: number, bucketS: number): boolean {
  if (bucketS >= 86400) return false
  if (tEnd - t0 > 24 * 3_600_000) return true
  return bucketS >= 3600 && jourParis(t0) !== jourParis(Math.max(t0, tEnd - 1))
}

/** « mer. 15 · 14 h » quand le jour compte ; « 14:00 » sinon ; « 15 oct. » pour un jour. */
export function libelleDuBucket(tMs: number, bucketS: number, avecJour: boolean): string {
  const d = new Date(tMs)
  if (bucketS >= 86400) return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: PARIS })
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: PARIS })
  if (!avecJour) return heure
  const jour = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', timeZone: PARIS })
  return `${jour} · ${heure.endsWith(':00') ? `${Number(heure.slice(0, 2))} h` : heure}`
}

/** Le titre du panneau de lecture : « mercredi 15 octobre · 14:00 » ou « 15 octobre ». */
export function titreDuBucket(tMs: number, bucketS: number, avecJour: boolean): string {
  const d = new Date(tMs)
  if (bucketS >= 86400) return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: PARIS })
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: PARIS })
  return avecJour ? `${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: PARIS })} · ${heure}` : heure
}

/**
 * LE CUMUL EMPILÉ — PUR. Chaque série est cumulée, puis posée sur les
 * précédentes : le bord haut de la dernière couche est le total de la nuit
 * (« Cumul » traçait une courbe par série et jamais leur somme).
 * Renvoie, par série, le bas et le haut de sa couche à chaque intervalle.
 */
export function couchesEmpilees(valeurs: readonly (readonly number[])[], cumuler: boolean): { bas: number[]; haut: number[] }[] {
  const n = valeurs[0]?.length ?? 0
  const base = new Array<number>(n).fill(0)
  return valeurs.map((v) => {
    const propres = [...v]
    if (cumuler) { let acc = 0; for (let i = 0; i < n; i++) { acc += propres[i] ?? 0; propres[i] = acc } }
    const bas = [...base]
    const haut = bas.map((b, i) => b + (propres[i] ?? 0))
    for (let i = 0; i < n; i++) base[i] = haut[i]!
    return { bas, haut }
  })
}
